import React, { useEffect, useState } from 'react';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  OverlayDrawer,
  Select,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ContractNegotiationVersionConcept } from '../../../models/Entities/Contracts/ContractNegotiationVersionConcept';
import { NewContractNegotiationVersionConcept } from '../../../models/Entities/Contracts/NewContractNegotiationVersionConcept';
import { useContractNegotiationVersionConceptFormStyles } from './ContractNegotiationVersionConceptForm.Styles';

interface Props {
  contractNegotiationVersionId: string;
  contractNegotiationId: string;
  contractId: number;
  versionConcept?: ContractNegotiationVersionConcept;
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (entity: NewContractNegotiationVersionConcept | ContractNegotiationVersionConcept) => void;
}

const ContractNegotiationVersionConceptFormComponent = (props: Props) => {
  const { t } = useTranslation();
  const styles = useContractNegotiationVersionConceptFormStyles();

  const [contractNegotiationConceptId, setContractNegotiationConceptId] = useState<string>('');
  const [currencyCode, setCurrencyCode] = useState<string>('');
  const [sponsorCost, setSponsorCost] = useState<number>(0);
  const [sponsorReferenceVariance, setSponsorReferenceVariance] = useState<number>(0);
  const [siteCost, setSiteCost] = useState<number>(0);
  const [siteReferenceVariance, setSiteReferenceVariance] = useState<number>(0);
  const [negotiationConceptStatus, setNegotiationConceptStatus] = useState<number>(0);

  useEffect(() => {
    if (props.isOpen) {
      setContractNegotiationConceptId(
        props.versionConcept?.contractNegotiationConceptId || ''
      );
      setCurrencyCode(props.versionConcept?.currencyCode || '');
      setSponsorCost(
        typeof props.versionConcept?.sponsorCost === 'number' ? props.versionConcept.sponsorCost : 0
      );
      setSponsorReferenceVariance(
        typeof props.versionConcept?.sponsorReferenceVariance === 'number'
          ? props.versionConcept.sponsorReferenceVariance
          : 0
      );
      setSiteCost(
        typeof props.versionConcept?.siteCost === 'number' ? props.versionConcept.siteCost : 0
      );
      setSiteReferenceVariance(
        typeof props.versionConcept?.siteReferenceVariance === 'number'
          ? props.versionConcept.siteReferenceVariance
          : 0
      );
      setNegotiationConceptStatus(
        typeof props.versionConcept?.negotiationConceptStatus === 'number'
          ? props.versionConcept.negotiationConceptStatus
          : 0
      );
    }
  }, [props.isOpen, props.versionConcept]);

  const handleSave = () => {
    const base: NewContractNegotiationVersionConcept = {
      contractNegotiationVersionId: props.contractNegotiationVersionId,
      contractNegotiationConceptId,
      currencyCode: currencyCode || undefined,
      sponsorCost,
      sponsorReferenceVariance,
      siteCost,
      siteReferenceVariance,
      negotiationConceptStatus,
    };

    if (props.versionConcept?.id) {
      props.onSave({ ...props.versionConcept, ...base });
    } else {
      props.onSave(base);
    }
  };

  const isEdit = !!props.versionConcept?.id;
  const dialogTitle = isEdit
    ? t('contractNegotiationVersionConceptForm.titleEdit', 'Editar concepto de versión')
    : t('contractNegotiationVersionConceptForm.titleNew', 'Nuevo concepto de versión');

  return (
    <OverlayDrawer position="end" size="medium" open={props.isOpen}
      onOpenChange={(_, { open }) => { if (!open) props.onDismiss(); }}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={<Button appearance="subtle" icon={<DismissRegular />} onClick={props.onDismiss} />}>
          {dialogTitle}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.form}>
          <div className={styles.formRow}>
            <Field
              label={t(
                'contractNegotiationVersionConceptForm.fields.conceptId',
                'ID Concepto de negociación'
              )}
            >
              <Input
                value={contractNegotiationConceptId}
                onChange={(_, data) => setContractNegotiationConceptId(data.value)}
                placeholder={t(
                  'contractNegotiationVersionConceptForm.placeholders.conceptId',
                  'Ingrese ID'
                )}
              />
            </Field>
            <Field
              label={t(
                'contractNegotiationVersionConceptForm.fields.currencyCode',
                'Código de moneda'
              )}
            >
              <Input
                value={currencyCode}
                onChange={(_, data) => setCurrencyCode(data.value)}
                placeholder="ARS"
              />
            </Field>
          </div>
          <div className={styles.formRow}>
            <Field
              label={t(
                'contractNegotiationVersionConceptForm.fields.sponsorCost',
                'Costo sponsor'
              )}
            >
              <Input
                type="number"
                value={String(sponsorCost)}
                onChange={(_, data) => setSponsorCost(Number(data.value))}
              />
            </Field>
            <Field
              label={t(
                'contractNegotiationVersionConceptForm.fields.sponsorVariance',
                'Varianza sponsor (%)'
              )}
            >
              <Input
                type="number"
                value={String(sponsorReferenceVariance)}
                onChange={(_, data) => setSponsorReferenceVariance(Number(data.value))}
              />
            </Field>
          </div>
          <div className={styles.formRow}>
            <Field
              label={t(
                'contractNegotiationVersionConceptForm.fields.siteCost',
                'Costo sitio'
              )}
            >
              <Input
                type="number"
                value={String(siteCost)}
                onChange={(_, data) => setSiteCost(Number(data.value))}
              />
            </Field>
            <Field
              label={t(
                'contractNegotiationVersionConceptForm.fields.siteVariance',
                'Varianza sitio (%)'
              )}
            >
              <Input
                type="number"
                value={String(siteReferenceVariance)}
                onChange={(_, data) => setSiteReferenceVariance(Number(data.value))}
              />
            </Field>
          </div>
          <div className={styles.formRow}>
            <Field
              label={t(
                'contractNegotiationVersionConceptForm.fields.status',
                'Estado del concepto'
              )}
            >
              <Select
                value={String(negotiationConceptStatus)}
                onChange={(_, data) => setNegotiationConceptStatus(Number(data.value))}
              >
                <option value="0">
                  {t('contractNegotiationVersionConceptForm.status.pending', 'Pendiente')}
                </option>
                <option value="1">
                  {t('contractNegotiationVersionConceptForm.status.accepted', 'Aceptado')}
                </option>
                <option value="2">
                  {t('contractNegotiationVersionConceptForm.status.rejected', 'Rechazado')}
                </option>
              </Select>
            </Field>
          </div>
        </div>
        <div className={styles.formActions}>
          <Button appearance="secondary" onClick={props.onDismiss}>
            {t('contractNegotiationVersionConceptForm.actions.cancel', 'Cancelar')}
          </Button>
          <Button appearance="primary" onClick={handleSave}>
            {t('contractNegotiationVersionConceptForm.actions.save', 'Guardar')}
          </Button>
        </div>
      </DrawerBody>
    </OverlayDrawer>
  );
};

export default ContractNegotiationVersionConceptFormComponent;
